import {
  createInvoice,
  depositToLineItem,
} from '@principle-theorem/principle-core';
import {
  TransactionProvider,
  type IAccountCredit,
  type IInvoice,
  type IPatient,
  type IPractice,
  type IStaffer,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { type IResolvedTransactionOption } from '../transaction-providers/transaction-provider';
import { type TransactionProviders } from '../transaction-providers/transaction-providers.service';

export interface ICapturedAccountCredit {
  invoice: WithRef<IInvoice>;
  transaction: DocumentReference<ITransaction>;
}

export enum AccountCreditCaptureStatus {
  Captured = 'captured',
  /** Provably nothing was written. Any claim taken has been released. */
  Abandoned = 'abandoned',
  /** Capture produced no transaction and cannot prove why. Claims are kept. */
  Indeterminate = 'indeterminate',
}

export type AccountCreditCaptureOutcome =
  | ({ status: AccountCreditCaptureStatus.Captured } & ICapturedAccountCredit)
  | { status: AccountCreditCaptureStatus.Abandoned }
  | { status: AccountCreditCaptureStatus.Indeterminate };

/**
 * Providers whose `capture()` is known to return before it writes anything.
 *
 * `ITransactionProvider.capture()` resolves `undefined` for several unrelated
 * outcomes — a cancelled dialog, a declined card, a terminal that never
 * answered — and Stripe persists a transaction for a failed payment before
 * returning `undefined`. A falsy return therefore cannot be read as "no money
 * moved". These two providers are listed because their cancel branch is a bare
 * `return` ahead of `addTransactionToInvoice`, which is verifiable by reading
 * them; every other provider stays indeterminate. Read the provider before
 * adding to this list — an entry that writes on cancel would let a second
 * capture take the patient's deposit twice.
 */
const providersWithAuditedCancel: TransactionProvider[] = [
  TransactionProvider.Manual,
  TransactionProvider.Cash,
];

export interface IAccountCreditCaptureRequest {
  option: IResolvedTransactionOption;
  credit: IAccountCredit;
  patient: WithRef<IPatient>;
  practice: WithRef<IPractice>;
  staffer: WithRef<IStaffer>;
  transactions: TransactionProviders;
  /**
   * Optional appointment-deposit gate. When present it must resolve before any
   * invoice exists, so the appointment owns exactly one deposit invoice.
   */
  acquireInvoiceClaim?: () => Promise<void>;
  /**
   * Undoes {@link acquireInvoiceClaim}. Only ever called once the capture is
   * known to have written nothing, so the appointment is left able to try
   * again rather than holding a claim nobody will finish.
   */
  abandonInvoiceClaim?: () => Promise<void>;
}

/**
 * Owns the invoice-then-capture sequence shared by every surface that turns an
 * account credit into money: the account-credit dialog and the appointment
 * deposit capture panel. Keeping one implementation means the claim ordering
 * and the cancelled-capture cleanup cannot drift between hosts.
 */
export class AccountCreditCapture {
  /**
   * Creates the credit's invoice and captures against it, reporting which of
   * the three outcomes occurred rather than collapsing two of them onto
   * `undefined`.
   *
   * The claim is taken here, but the provider it guards may still open its own
   * UI and wait on a person — the manual and cash providers both do. The lease
   * is therefore held across an open-ended human pause with a Cancel button in
   * the middle of it, and abandoning that pause has to leave the appointment
   * able to try again. Releasing the claim on any capture that merely returned
   * nothing would not be safe; see {@link providersWithAuditedCancel} for why
   * only some providers can be believed.
   */
  static async capture(
    request: IAccountCreditCaptureRequest
  ): Promise<AccountCreditCaptureOutcome> {
    await request.acquireInvoiceClaim?.();
    const invoice = await AccountCreditCapture._invoiceForCredit(request);
    const transaction = await request.transactions.capture(
      request.option.provider,
      invoice
    );
    if (transaction) {
      return {
        status: AccountCreditCaptureStatus.Captured,
        invoice,
        transaction,
      };
    }
    // Discarding the invoice first means a failure to clean it up throws before
    // anything is released, leaving the claim in place for a human to inspect.
    await AccountCreditCapture._discardInvoice(invoice);
    if (
      !providersWithAuditedCancel.includes(request.option.provider.providerId)
    ) {
      return { status: AccountCreditCaptureStatus.Indeterminate };
    }
    await request.abandonInvoiceClaim?.();
    return { status: AccountCreditCaptureStatus.Abandoned };
  }

  private static async _discardInvoice(
    invoice: WithRef<IInvoice>
  ): Promise<void> {
    try {
      await Firestore.deleteDoc(invoice.ref);
    } catch (error) {
      // eslint-disable-next-line no-console -- cleanup failure is a money-integrity incident and must be visible without adding a logger dependency to this UI library.
      console.error(
        `[CRITICAL] Failed to delete cancelled deposit invoice ${invoice.ref.path}`,
        error
      );
      throw error;
    }
  }

  private static async _invoiceForCredit(
    request: IAccountCreditCaptureRequest
  ): Promise<WithRef<IInvoice>> {
    const credit = request.credit;
    const depositLineItem = depositToLineItem(
      credit.description,
      credit.amount,
      credit.reservedFor?.practitioner,
      credit.reservedFor?.treatmentCategory,
      undefined,
      credit.reservedFor?.appointmentRef
    );
    return createInvoice(request.patient, request.practice, request.staffer, [
      depositLineItem,
    ]);
  }
}
